import React, { useMemo, useRef, useState } from 'react'
import { Button, Divider, Icon, Popup, Progress, Tab, Modal, Form } from 'semantic-ui-react'
import XLSX from 'xlsx';
import { GetDefaultReservationValues, GetHotelAddons, GetRooms, GetRoomTypes, SaveReservation } from '../../api/services/pms';
import GridPanel from '../../components/GridPanel';
import PropTypes from 'prop-types'
import './style.css'
import moment from 'moment'
import { GetHotelParameters } from '../../api/services/organization';
import { GetCustomers, SearchGuest, CreateGuest } from '../../api/services/crm';
import { GetNationalities, GetCountries } from '../../api/services/shared';
import { onWarning, parseException } from '../../components/shared/messages';
import { useTextResources } from '../../language';
import { dateFormat, ReservationAmountOverrideType } from '../../components/shared/constants';

/* eslint-disable react/display-name */
const ImportExcelPage = ({ HotelId }) => {
  const fileInputRef = useRef()
  const [reservationsData, setReservationsData] = useState()
  const [addonsData, setAddonsData] = useState()
  const [guestsData, setGuestsData] = useState()
  const [progress, setProgress] = useState(0)
  const [label, setLabel] = useState("Waiting to begin processing")
  const [mappingState, setMappingState] = useState({
    showMapping: false,
    reservations: null,
    addons: null,
    guests: null
  })
  const text = useTextResources()

  const statuses = {
    "Not Saved": <Icon name="star outline" />,
    "Skipped": <Icon name="ban" />,
    "Saving": <Icon loading name='spinner' />,
    "Saved": <Icon name="check circle" color='green' />
  }

  /* eslint-disable react/prop-types */
  const StatusDisplay = ({ item }) => {
    if (item.SaveResult === "Error") {
      return <Popup on="click" hoverable trigger={<div><Icon name="warning sign" color="red" link />{item.ErrorMessage}</div>} content={
        <Popup.Content style={{ maxHeight: 300, overflow: "auto", maxWidth: 450 }}>
          {item.ErrorMessage} 
        </Popup.Content>} />
    } else {
      return statuses[item.SaveResult || "Not Saved"]
    }
  }
  /* eslint-enable react/prop-types */

  const continueImport = () => {
    const workbook = mappingState.workbook
    const reservations = workbook.Sheets[mappingState.reservations];
    let error = false
    if (reservations) {
      const data = XLSX.utils.sheet_to_json(reservations)
      setReservationsData(data)
    } else if (mappingState.reservations) {
      error = true
      onWarning(`Could not load reservation data, please make sure a sheet with the name '${mappingState.reservations}' is present in the file`)
    }

    const addons = workbook.Sheets[mappingState.addons];
    if (addons) {
      const data = XLSX.utils.sheet_to_json(addons)
      setAddonsData(data)
    } else if (mappingState.addons) {
      error = true
      onWarning(`Could not load addons data, please make sure a sheet with the name '${mappingState.addons}' is present in the file`)
    }

    const guests = workbook.Sheets[mappingState.guests];
    if (guests) {
      const data = XLSX.utils.sheet_to_json(guests)
      setGuestsData(data)
    } else if (mappingState.guests) {
      error = true
      onWarning(`Could not load guests data, please make sure a sheet with the name '${mappingState.guests}' is present in the file`)
    }
    if (!error) {
      setMappingState({
        ...mappingState,
        workbook: null,
        showMapping: false
      })
    }
  }

  const fileChange = event => {
    if (event.target.files && event.target.files.length) {
      const f = event.target.files[0]
      var reader = new FileReader();
      reader.onload = function (e) {
        var arr = new Uint8Array(e.target.result);
        var workbook = XLSX.read(arr, { type: 'array', cellDates: true });
        setMappingState({
          ...mappingState,
          workbook,
          reservations: workbook.SheetNames.includes("Reservations") && "Reservations",
          addons: workbook.SheetNames.includes("Addons") && "Addons",
          guests: workbook.SheetNames.includes("Guests") && "Guests",
          showMapping: true
        })
        fileInputRef.current.value = null
      }
      reader.readAsArrayBuffer(f);
    }
  }
  const patchDate = value => {
    if (value) {
      const offset = value.getTimezoneOffset()
      return moment(value).subtract(offset, "minutes")
    } else return moment()
  }

  const dateFormatter = ({ value }) => {
    return patchDate(value).format(dateFormat)
  }

  const options = useMemo(() => {
    return {
      columns: [
        { name: 'OrderID', title: "Order ID" },
        { name: 'GuestName', title: "Guest" },
        { name: 'CheckIn', title: "Check In", formatter: dateFormatter },
        { name: 'CheckOut', title: "Check Out", formatter: dateFormatter },
        { name: 'RoomType', title: "Room Type" },
        { name: 'RoomName', title: "Room Name" },
        {
          name: 'PAX', title: "PAX", getCellValue: row => {
            return `${row.Adults}\\${row.Children}`
          }
        },
        {
          name: 'Price', title: "Price", formatter: ({ value, row }) => {
            return `${Number(value).toFixed(2)} ${row.Currency || "NIS"}`
          }
        },
        {
          name: 'Addons', title: "Num Addons", getCellValue: row => {
            if (addonsData) {
              const reservationAddons = addonsData.filter(x => x.OrderID === row.OrderID && x.ResLineId === row.LineID)
              return reservationAddons.length
            } else return 0
          }
        },
        {
          name: 'ErrorMessage', title: "Process", formatter: ({ row }) => {
            return <StatusDisplay item={row} />
          }
        },
      ],
      columnExtensions: [
        { columnName: 'ErrorMessage', width: 500 }
      ],
      export: true,
      noVirtualTable: true
    }
  })

  const guestOptions = useMemo(() => {
    return {
      columns: [
        { name: 'FullName', title: "Full Name" },
        { name: 'Email', title: "Email" },
        { name: 'Tel1', title: "Phone" },
        { name: 'Address', title: "Address" },
        { name: 'Country', title: "Country" },
        {
          name: "Process", title: "Process",
          getCellValue: row => {
            return <StatusDisplay item={row} />
          }
        },
      ],
      columnExtensions: [
        { columnName: 'Process', width: 500 }
      ],
      noVirtualTable: true
    }
  })

  const isValidEmail = email => {
    var pattern = new RegExp(/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);
    return email && pattern.test(email);
  }


  /* eslint-disable require-atomic-updates */
  const startImport = async () => {
    setProgress(0)
    // Get all nessesarry default options
    setLabel("Getting default parameters")
    const [roomTypes, hotelParams, defaults, nationalities, hotelAddons, rooms, customers, countries] = await Promise.all([
      GetRoomTypes({ HotelID: HotelId }),
      GetHotelParameters({ ID: HotelId }),
      GetDefaultReservationValues({ HotelId: HotelId }),
      GetNationalities(),
      GetHotelAddons({ HotelId: HotelId, IncludeAddonChain: true, IncludeHotelAddon: true }),
      GetRooms({ HotelID: HotelId }),
      GetCustomers({ GetOnlyCustomerArray: true }),
      GetCountries({ IncludeStates: false })
    ])
    if (guestsData) {
      // Process guests
      setLabel("Importing guests")
      for (var j = 0; j < guestsData.length; j++) {
        var guest = guestsData[j]
        if (guest.SaveResult !== "Saved") {
          try {
            setLabel(`Processing gust: ${guest.FullName}`)
            guest.SaveResult = "Saving"
            setGuestsData([...guestsData])
            let vals = guest.FullName ? guest.FullName?.trim().split(" ") : [];
            const nationality = guest.Country ? nationalities.find(x => x.Name === guest.Country?.trim()) : null
            const country = guest.Country ? countries.find(x => x.Name === guest.Country?.trim()) : null
            const email = guest.Email ? guest.Email?.trim() : ""
            const tel = guest.Tel1 ? guest.Tel1?.trim() : ""
            let existing = []
            if (email || tel) {
              existing = await SearchGuest({
                GuestPhone: tel,
                GuestEmail: email
              })
            } else if (guest.FullName) {
              existing = await SearchGuest({
                GuestFirstName: vals[0],
                GuestLastName: vals.slice(1).join(" ") || "-"
              })
            }
            if (existing.length === 0) {
              const newGuest = {
                ID: 0,
                FirstName: vals[0],
                LastName: vals.slice(1).join(" ") || "-",
                Phone: guest.Tel1 ? guest.Tel1?.trim() : null,
                Phone2: guest.Tel2 ? guest.Tel2?.trim() : null,
                Email: isValidEmail(email) ? email : null,
                NationalityID: nationality ? nationality.Id : hotelParams.CurrentHotel[0].HotelParameter.DefaultGuestNationalityID,
                WeddingDate: guest.Anniversary ? moment(guest.Anniversary).toISOString() : null,
                PassportNumber: guest.Passport,
                Location: {
                  ID: 0,
                  City: guest.City,
                  StreetAndHomeNumber: guest.Address,
                  CountryID: country ? country.Id : null,
                  Zip: guest.ZipCOde
                },
                GuestNotes: guest.Remark ? [{
                  Id: 0,
                  Content: guest.Remark?.trim()
                }
                ] : null
              }
              const result = await CreateGuest(newGuest)
              console.log(result)
              guest.SaveResult = "Saved"
            } else {
              guest.SaveResult = "Skipped"
            }
            setGuestsData([...guestsData])
          } catch (error) {
            guest.SaveResult = "Error"
            guest.ErrorMessage = error.error ? error.error.ErrorMessage : error.toString()
            setGuestsData([...guestsData])
          }
        }
        setProgress(Math.round(j / guestsData.length * 100))
      }
      setProgress(0)
    }
    for (var i = 0; i < reservationsData.length; i++) {
      var reservation = reservationsData[i]
      if (reservation.SaveResult !== "Saved") {
        try {
          setLabel(`Processing reservation: ${reservation.OrderID}`)
          reservation.SaveResult = "Saving"
          setReservationsData([...reservationsData])
          const roomType = reservation.RoomType ? roomTypes.find(x => x.Name.toLowerCase() === reservation.RoomType?.trim().toLowerCase()) : null
          if (!roomType) {
            throw `Room Type not found: ${reservation.RoomType}`
          }

          const productDefaults = defaults.HotelDefaultReservationProductsViewModel.find(x => x.RoomTypeId === roomType.Id)
          if (!productDefaults) {
            throw `Product defaults not found for room type: ${roomType.Name}`
          }
          const room = rooms.find(x => x.Name.toLowerCase() === String(reservation.RoomName).trim().toLowerCase())
          const agentname = reservation.Agent ? reservation.Agent?.trim().toLowerCase() : null;
          const agent = agentname ? customers.find(x => x.Name.toLowerCase() === reservation.Agent?.trim().toLowerCase()) : null
          const company = customers.find(x => x.Id === defaults.HotelDefaultReservationValuesViewModel.CompanyID)

          if (!room) {
            throw `Room with name "${reservation.RoomName}" not found`
          }

          
          const newReservation = {
            Id: 0,
            AgentID: agent ? agent.Id : defaults.HotelDefaultReservationValuesViewModel.AgentID,
            CompanyID: defaults.HotelDefaultReservationValuesViewModel.CompanyID,
            HotelId: HotelId,
            RoomTypeId: roomType.Id,
            CheckInDate: patchDate(reservation.CheckIn).toISOString(),
            CheckOutDate: patchDate(reservation.CheckOut).toISOString(),
            Eta: hotelParams.CurrentHotel[0].Eta,
            Edt: hotelParams.CurrentHotel[0].HotelParameter.Etd,
            Adults: reservation.Adults,
            ProductId: productDefaults.ProductID,
            Children: reservation.Children,
            Infants: reservation.Infants,
            Comments: reservation.Comments,
            OverriddenTotalAmount: Number(reservation.Price),
            ReservationPricingTypeID: Number(reservation.Price) == 0 ? ReservationAmountOverrideType.Free : ReservationAmountOverrideType.AmountByTotal,
            IsOverriddenTotalAmountAfterTax: true,
            OverrideProductPrice: true,
            RoomId: room?.Id,
            ReservationDailyAddons: [],
            PaymentTypeID: defaults.PaymentTypeID,
            PaymentArrangementID: agent?.DefaultPaymentArrangementID || company?.DefaultPaymentArrangementID,
            WingID: room?.WingID
          }

          // Get existing guest if needed
          if (reservation.GuestEmail || reservation.GuestPhone) {
            const guests = await SearchGuest({
              GuestPhone: reservation.GuestPhone ? String(reservation.GuestPhone)?.trim() : null,
              GuestEmail: reservation.GuestEmail ? reservation.GuestEmail?.trim() : null
            })
            if (guests.length) {
              newReservation.GuestId = guests[0].GuestID
            }
          }

          if (!newReservation.GuestId) {
            const nationality = reservation.Country ? nationalities.find(x => x.Name === reservation.Country?.trim()) : null
            let vals = reservation.GuestName ? reservation.GuestName?.trim().split(" ") : [];
            if (vals.length > 1) {
              newReservation.Guest = {
                ID: 0,
                FirstName: vals[0],
                LastName: vals.slice(1).join(" "),
                Phone: reservation.GuestPhone ? String(reservation.GuestPhone)?.trim() : null,
                Email: reservation.GuestEmail ? reservation.GuestEmail?.trim() : null,
                NationalityID: nationality ? nationality.Id : hotelParams.CurrentHotel[0].HotelParameter.DefaultGuestNationalityID
              }
            }
          }
          if (addonsData) {
            // Get addons related to reservation
            const reservationAddons = addonsData.filter(x => x.OrderID === reservation.OrderID && x.ResLineId === reservation.LineID)
            reservationAddons.forEach(addon => {
              // Find related hotel addon
              if (addon.AddonName) {
                const hotelAddon = hotelAddons.find(x => x.ChainAddon.Name === addon.AddonName?.trim())
                if (hotelAddon) {
                  const newAddon = {
                    Id: 0,
                    HotelAddonId: hotelAddon.Id,
                    CurrencyId: hotelParams.CurrentHotel[0].MainCurrencyId,
                    Date: patchDate(addon.Date),
                    Quantity: (addon.AdultQuantity || addon.ChildQuantity) ? null : addon.Quantity || null,
                    AdultQuantity: addon.AdultQuantity || null,
                    ChildQuantity: addon.ChildQuantity || null
                  }
                  if (addon.AdultQuantity || addon.ChildQuantity) {
                    newAddon.AdultAmount = addon.AdultPrice
                    newAddon.ChildAmount = addon.ChildPrice
                  } else {
                    newAddon.RoomAmount = addon.UnitPrice
                    newAddon.OriginalRoomAmount = hotelAddon.RoomAmount
                  }
                  newReservation.ReservationDailyAddons.push(newAddon)
                }
              }
            })
          }


          const result = await SaveReservation(newReservation)
          console.log(result)
          reservation.SaveResult = "Saved"
          setReservationsData([...reservationsData])
        } catch (error) {
          const details = parseException(error)
          reservation.SaveResult = "Error"
          reservation.ErrorMessage = details.description
          setReservationsData([...reservationsData])
        }
      }
      setProgress(Math.round(i / reservationsData.length * 100))
    }
    setProgress(100)
    setLabel("Finita La Commedia!")
  }
  /* eslint-enable require-atomic-updates */

  const sheetOptions = mappingState.workbook?.SheetNames?.map(x => ({ text: x, value: x })) || []
  const placeholder = "Please Select"

  return <div className="import-excel" style={{overflow: "auto" }}>
    <div style={{ display: "flex" }}>
      <Button
        positive
        content="Select Import File"
        labelPosition="left"
        icon="file excel"
        onClick={() => fileInputRef.current.click()}
      />
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".csv, .xls, .xlsx"
        onChange={fileChange}
      />
      <Button disabled={!reservationsData && !guestsData} onClick={startImport}>Start Import</Button>
      <Progress style={{ flex: 1 }} progress="percent" indicating percent={progress}>{label}</Progress>
    </div>
    <Divider />
    <Tab panes={[
      {
        menuItem: "Guests",
        render: () => <Tab.Pane>
          <GridPanel data={guestsData} options={guestOptions}/>
        </Tab.Pane>
      },
      {
        menuItem: "Reservations",
        render: () => <Tab.Pane>
          <GridPanel data={reservationsData} options={options}/>
        </Tab.Pane>
      }
    ]} />
    <Modal size="mini" open={mappingState.showMapping}>
      <Modal.Header>Map Excel Sheets</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Select clearable label={<span>Sheet for <b>Reservation</b> data</span>} onChange={(e, { value }) => {
            setMappingState({
              ...mappingState,
              reservations: value
            })
          }} options={sheetOptions} defaultValue={mappingState.reservations} placeholder={placeholder} />

          <Form.Select clearable label={<span>Sheet for <b>Addons</b> data</span>} onChange={(e, { value }) => {
            setMappingState({
              ...mappingState,
              addons: value
            })
          }} options={sheetOptions} defaultValue={mappingState.addons} placeholder={placeholder} />

          <Form.Select clearable label={<span>Sheet for <b>Guest</b> data</span>} onChange={(e, { value }) => {
            setMappingState({
              ...mappingState,
              guests: value
            })
          }} options={sheetOptions} defaultValue={mappingState.guests} placeholder={placeholder} />
        </Form>

      </Modal.Content>
      <Modal.Actions>
        <Button size={"small"} negative onClick={() => setMappingState({
          ...mappingState,
          workbook: null,
          showMapping: false
        })}>{text("Close")}</Button>
        <Button size={"small"} positive onClick={continueImport}>{text("Continue")}</Button>
      </Modal.Actions>
    </Modal>
  </div>
}
/* eslint-enable react/display-name */
ImportExcelPage.propTypes = {
  HotelId: PropTypes.any
}
export default ImportExcelPage